// frontend/src/components/bossBattles/BattleResults.js
// Component to display boss battle results

import React from 'react';
import './BattleResults.css';

/**
 * BattleResults - Displays the outcome of a resolved boss battle
 * 
 * @param {Object} battleLog - Battle log data from the API
 * @param {Array} participants - List of participants with their contributions
 * @param {Array} bossHeads - Boss heads data (if available)
 * @param {Function} onClaimReward - Callback to claim mythril reward
 * @param {Object} userParticipant - Current user's participant data
 */
function BattleResults({ battleLog, participants, bossHeads, onClaimReward, userParticipant }) {
    // VALIDATE REQUIRED PROPS
    if (!battleLog) {
        throw new Error('❌ BattleResults: battleLog prop is required');
    }
    
    const requiredFields = ['victory', 'event_log'];
    requiredFields.forEach(field => {
        if (battleLog[field] === undefined) {
            throw new Error(`❌ BattleResults: battleLog.${field} is required`);
        }
    });
    
    const isVictory = battleLog.victory;
    const eventLog = battleLog.event_log || [];
    const totalDamage = battleLog.total_damage_dealt || 0;
    const bossFinalHealth = battleLog.boss_final_health || 0;
    
    const canClaimReward = userParticipant && 
                          userParticipant.mythril_reward > 0 && 
                          !userParticipant.reward_claimed;
    
    return (
        <div className="battle-results">
            {/* Victory/Defeat Banner */}
            <div className={`result-banner ${isVictory ? 'victory' : 'defeat'}`}>
                <div className="result-icon">
                    {isVictory ? '🏆' : '💀'}
                </div>
                <h2 className="result-title">
                    {isVictory ? 'VICTORY!' : 'DEFEAT'}
                </h2>
                <p className="result-subtitle">
                    {isVictory 
                        ? 'The boss has been vanquished!' 
                        : 'The boss proved too powerful...'}
                </p>
            </div>
            
            {/* Battle Statistics */}
            <div className="battle-stats-section">
                <h3 className="section-title">Battle Statistics</h3>
                
                <div className="stat-grid">
                    <div className="stat-item">
                        <span className="stat-label">Total Damage</span>
                        <span className="stat-value damage-value">
                            {totalDamage.toLocaleString()}
                        </span>
                    </div>
                    
                    {!isVictory && (
                        <div className="stat-item">
                            <span className="stat-label">Boss Remaining HP</span>
                            <span className="stat-value health-value">
                                {bossFinalHealth.toLocaleString()}
                            </span>
                        </div>
                    )}
                    
                    {participants && (
                        <div className="stat-item">
                            <span className="stat-label">Warriors</span>
                            <span className="stat-value">
                                {participants.length}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Boss Heads Defeated */}
            {bossHeads && bossHeads.length > 0 && (
                <div className="boss-heads-section">
                    <h3 className="section-title">Boss Heads</h3>
                    <div className="heads-list">
                        {bossHeads.map((head, index) => (
                            <div key={index} className="head-item">
                                <span className="head-name">{head.head_name}</span>
                                <div className="head-health-bar">
                                    <div 
                                        className="head-health-fill"
                                        style={{ 
                                            width: `${(head.current_health / head.max_health) * 100}%` 
                                        }}
                                    />
                                </div>
                                <span className="head-health-text">
                                    {head.current_health}/{head.max_health}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Participant Contributions */}
            {participants && participants.length > 0 && (
                <div className="contributions-section">
                    <h3 className="section-title">Warrior Contributions</h3>
                    <div className="participants-list">
                        {participants
                            .sort((a, b) => (b.damage_dealt || 0) - (a.damage_dealt || 0))
                            .map((participant, index) => (
                                <div 
                                    key={participant.id} 
                                    className={`participant-row ${userParticipant?.id === participant.id ? 'current-user' : ''}`}
                                >
                                    <span className="participant-rank">#{index + 1}</span>
                                    <div className="participant-info">
                                        <span className="participant-name">
                                            {participant.user_display_name || `Warrior ${participant.id}`}
                                        </span>
                                        <span className="participant-role">{participant.role}</span>
                                    </div>
                                    <span className="participant-damage">
                                        {(participant.damage_dealt || 0).toLocaleString()} dmg
                                    </span>
                                    {participant.mythril_reward > 0 && (
                                        <span className="participant-reward">
                                            +{participant.mythril_reward} ⚡
                                        </span>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>
            )}
            
            {/* Reward Claim Section */}
            {userParticipant && userParticipant.mythril_reward > 0 && (
                <div className="reward-section">
                    <h3 className="section-title">Your Reward</h3>
                    <div className="reward-container">
                        <div className="reward-amount">
                            <span className="reward-icon">⚡</span>
                            <span className="reward-value">{userParticipant.mythril_reward}</span>
                            <span className="reward-label">Mythril</span>
                        </div>
                        {canClaimReward ? (
                            <button 
                                className="claim-button"
                                onClick={onClaimReward}
                            >
                                Claim Reward
                            </button>
                        ) : userParticipant.reward_claimed ? (
                            <div className="claimed-message">
                                ✓ Reward Claimed
                            </div>
                        ) : (
                            <div className="no-reward-message">
                                No reward available
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Battle Event Log */}
            {eventLog.length > 0 && (
                <div className="event-log-section">
                    <h3 className="section-title">Battle Log</h3>
                    <div className="event-log">
                        {eventLog.map((event, index) => (
                            <div key={index} className="event-entry">
                                <span className="event-text">{event}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default BattleResults;
